<template>
    <div class="card">
        <div class="card-body">
            <validation-observer v-slot="{ passes, invalid }">
                <form @submit.prevent="passes(save)">
                    <div class="top-invoice-header">
                        <h5 class="text-primary">
                            <div class="serial-h">
                                <fa-icon :icon="['fas', 'qrcode']" />
                                <g-inline-editor v-model="fields.serie" />
                                <a-tooltip placement="right">
                                    <template slot="title">
                                        <fa-icon icon="sync-alt" :spin="loadingSerial" />
                                        {{ loadingSerial ? 'Refrescando...' : 'Refrescar nº de serie' }}
                                    </template>
                                    <sup
                                        class="font-size-14 cursor"
                                        :class="loadingSerial ? 'text-muted' : 'text-primary'"
                                        @click="handlerRefreshSerial"
                                    >
                                        <fa-icon icon="sync-alt" :spin="loadingSerial" />
                                    </sup>
                                </a-tooltip>
                            </div>

                            <selector
                                v-if="changeSerieIsAvailable"
                                v-model="serieSelected"
                                :maxSelection="1"
                                :data="seriesNames"
                                placeholder="Seleccionar serie"
                                @input="handlerRefreshSerial"
                            />
                        </h5>

                        <div>
                            <span class="d-sm-block">Fecha de albaran:</span>
                            <g-field
                                v-model="fields.date"
                                :app="app"
                                :item="id"
                                name="date"
                                placeholder="Fecha"
                                :clean="true"
                            />
                        </div>
                    </div>

                    <hr class="mb-4 mt-3" />

                    <div class="row align-items-center">
                        <div class="col-2 d-none d-sm-block">
                            <img v-if="config.logo" class="h-auto w-100" :src="config.logo" />
                            <img v-else class="h-auto w-100" :src="$app.fallbackLogo" />
                        </div>

                        <div class="col-4 d-none d-sm-block">
                            <div class="flex flex-1 flex-col">
                                <h5>Emisor</h5>

                                <div class="invoice__recipient-info my-4">
                                    <span>
                                        {{ config.name ? config.name : 'MILAGROS LOMBERA GUTIÉRREZ,' }},
                                        {{ config.CIF ? config.CIF : '13644774R' }}
                                    </span>
                                    <br />
                                    <span>
                                        {{ config.address ? config.address : 'Barrio La Bárcena s/n' }}
                                    </span>
                                    <br />
                                    <span>
                                        {{ config.address2 ? config.address2 : '39840, Ampuero, Cantabria' }}
                                    </span>
                                </div>
                                <div class="invoice__recipient-contact">
                                    <p>
                                        <fa-icon :icon="['far', 'envelope']" />
                                        <span class="ml-2">
                                            {{ config.mail ? config.mail : 'marian@quesolasgarmillas.es' }}
                                        </span>
                                    </p>
                                    <p>
                                        <fa-icon :icon="['far', 'phone']" />
                                        <span class="ml-2">{{ config.phone ? config.phone : '942634291' }}</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="col col-sm-6 mt-sm-0 mt-4 invoiceForm">
                            <fieldset class="dashed m-t-20">
                                <label><i class="zmdi zmdi-account-circle" /> Información General</label>

                                <div class="clientStatus">
                                    <a-tag v-if="!isClientSelected"> Sin registrar </a-tag>
                                    <a-tag v-else color="green">
                                        {{ fields.client_name }}
                                        <fa-icon class="ml-1" :icon="['fas', 'times']" @click="resetClient" />
                                    </a-tag>
                                </div>

                                <div class="row mb-3">
                                    <div class="col">
                                        <g-field
                                            v-model="fields.client"
                                            :app="app"
                                            :item="id"
                                            table="clients"
                                            column-search="alias"
                                            name="client"
                                            placeholder="Cliente"
                                            ret-label="alias"
                                            :clean="true"
                                            @input="changeClient"
                                        />
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-8">
                                        <g-field
                                            v-model="fields.client_address"
                                            :app="app"
                                            :item="id"
                                            name="client_address"
                                            placeholder="Dirección"
                                            :clean="true"
                                        />
                                    </div>

                                    <div class="col-4">
                                        <g-field
                                            v-model="fields.client_cp"
                                            :app="app"
                                            :item="id"
                                            name="client_cp"
                                            placeholder="Código Postal"
                                            :clean="true"
                                        />
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col">
                                        <g-field
                                            v-model="fields.client_location"
                                            :app="app"
                                            :item="id"
                                            name="client_location"
                                            placeholder="Población"
                                            :clean="true"
                                        />
                                    </div>
                                    <div class="col">
                                        <g-field
                                            v-model="fields.client_state"
                                            :app="app"
                                            :item="id"
                                            name="client_state"
                                            placeholder="Provincia"
                                            :clean="true"
                                        />
                                    </div>
                                    <div class="col">
                                        <g-field
                                            v-model="fields.client_country"
                                            :app="app"
                                            :item="id"
                                            name="client_country"
                                            placeholder="Pais"
                                            :clean="true"
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </div>

                    <div class="mt-5 mb-2 row">
                        <div class="col text-right">
                            <a-button icon="plus" type="primary" @click="createConcept"> Añadir concepto </a-button>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table
                            class="conceptsTable table d-sm-table table-hover"
                            :class="{ 'table-striped': !$settings.dark }"
                        >
                            <thead>
                                <th>CONCEPTO</th>
                                <th>CANTIDAD</th>
                                <th v-if="showPrices">PRECIO</th>
                                <th v-if="showPrices">DESCUENTO</th>
                                <th colspan="2">LOTE</th>
                                <th v-if="showPrices" class="text-right">IVA</th>
                                <th v-if="showPrices" class="text-right">RECARGO</th>
                                <th v-if="showPrices" class="text-right">TOTAL</th>
                                <th />
                            </thead>

                            <tbody>
                                <tr v-for="(tr, i) in fields.concepts" :key="i" class="pb-3">
                                    <td :style="{ 'min-width': '190px' }">
                                        <g-field
                                            v-model="fields.concepts[i].article"
                                            ret-label="name"
                                            ret-key="code"
                                            :app="app"
                                            :item="id"
                                            :sync="false"
                                            type="relation"
                                            :name="'article'"
                                            table="articles"
                                            label="name"
                                            placeholder="Articulo"
                                            :order="{ order: 'asc' }"
                                            :limit="20"
                                            :clean="true"
                                            @select="articleChangeActions($event, i)"
                                        />
                                    </td>
                                    <td :style="{ 'min-width': '100px' }">
                                        <g-field
                                            v-model="fields.concepts[i].quantity"
                                            app="concept_invoices"
                                            :item="id"
                                            type="integer"
                                            step="1"
                                            :default-value="0"
                                            :disabled="!isArticleSelected[i]"
                                            :name="'quantity'"
                                            placeholder="Cantidad"
                                            :clean="true"
                                        />
                                    </td>

                                    <td v-if="showPrices" :style="{ 'min-width': '100px' }">
                                        <g-field
                                            v-model="fields.concepts[i].price"
                                            app="concept_invoices"
                                            :item="id"
                                            :default-value="0"
                                            preffix="€"
                                            :disabled="!isArticleSelected[i]"
                                            :name="'price'"
                                            placeholder="Precio"
                                            :clean="true"
                                        />
                                    </td>

                                    <td v-if="showPrices" :style="{ 'min-width': '50px' }">
                                        <g-field
                                            v-model="fields.concepts[i].discount"
                                            app="concept_invoices"
                                            :item="id"
                                            :default-value="0"
                                            preffix="%"
                                            :disabled="!isArticleSelected[i]"
                                            :name="'discount'"
                                            placeholder="Descuento"
                                            :clean="true"
                                        />
                                    </td>
                                    <td :style="{ 'min-width': '160px' }">
                                        <g-field
                                            ref="lotAutocomplete"
                                            v-model="fields.concepts[i].lot"
                                            :app="app"
                                            :item="id"
                                            type="relation"
                                            table="lots"
                                            column-search="name"
                                            name="name"
                                            placeholder="Lote"
                                            ret-label="name"
                                            :filter="lotFilter[i]"
                                            :clean="true"
                                            :disabled="!isArticleSelected[i]"
                                            :limit="20"
                                            @input="lotChange($event, i)"
                                            :order="{ expiration_date: 'desc' }"
                                        />
                                    </td>
                                    <td :style="{ 'min-width': '130px' }">
                                        <g-field
                                            v-model="fields.concepts[i].expiration_date"
                                            app="concept_invoices"
                                            :item="id"
                                            :disabled="!isArticleSelected[i]"
                                            :name="'expiration_date'"
                                            placeholder="Fecha de expiración"
                                            :clean="true"
                                        />
                                    </td>
                                    <td
                                        v-if="showPrices"
                                        class="text-right align-middle"
                                        :style="{ 'min-width': '100px' }"
                                    >
                                        {{ conceptCalculation[i].iva.value | price }} €
                                        <sup>{{ isArticleSelected[i] ? conceptCalculation[i].iva.percent : 0 }}%</sup>
                                    </td>
                                    <td
                                        v-if="showPrices"
                                        class="text-right align-middle"
                                        :style="{ 'min-width': '100px' }"
                                    >
                                        {{ conceptCalculation[i].surcharge.value | price }} €
                                        <sup>{{ conceptCalculation[i].surcharge.percent }}%</sup>
                                    </td>
                                    <td
                                        v-if="showPrices"
                                        class="text-right align-middle"
                                        :style="{ 'min-width': '100px' }"
                                    >
                                        {{ conceptCalculation[i].total | price }} €
                                    </td>
                                    <td class="text-center align-middle" :style="{ 'min-width': '50px' }">
                                        <a v-show="i !== '1'" @click="removeConcept(i)">
                                            <fa-icon :icon="['fas', 'times']" />
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="row">
                        <div class="col-12 col-sm-6">
                            <g-field
                                v-model="fields.observations"
                                type="textarea"
                                :app="app"
                                :item="id"
                                name="observations"
                                placeholder="Observaciones"
                                :clean="true"
                            />
                        </div>

                        <div class="col-12 col-sm-4 mt-4 mt-sm-0 offset-sm-2 align-self-end">
                            <div class="row">
                                <div class="col-12">
                                    <table v-if="showPrices" class="table table-bordered mb-3">
                                        <tbody>
                                            <tr>
                                                <th class="text-right">SUBTOTAL</th>
                                                <td class="text-right">{{ totalCalculations.subtotal | price }} €</td>
                                            </tr>
                                            <tr v-if="clientSurcharge > 0">
                                                <th class="text-right">
                                                    <sup>{{ totalCalculations.surcharge.percent }} %</sup> RECARGO
                                                </th>
                                                <td class="text-right">
                                                    {{ totalCalculations.surcharge.value | price }} €
                                                </td>
                                            </tr>
                                            <tr>
                                                <th class="text-right">
                                                    <sup>{{ totalCalculations.iva.percent }} %</sup> IVA
                                                </th>
                                                <td class="text-right">{{ totalCalculations.iva.value | price }} €</td>
                                            </tr>
                                            <tr>
                                                <th class="text-right">TOTAL</th>
                                                <td class="text-right">{{ totalCalculations.total | price }} €</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="row mt-3 d-none d-md-block">
                                <div class="col-12 col-sm-4">
                                    <g-field
                                        v-model="fields.price_delivered"
                                        :app="app"
                                        :item="id"
                                        name="price_delivered"
                                        title="Importe entregado"
                                    />
                                </div>
                                <div class="col-12 col-sm-4">
                                    <g-field
                                        v-model="fields.price_payed"
                                        :app="app"
                                        :item="id"
                                        name="price_payed"
                                        title="Importe pagado"
                                    />
                                </div>
                                <div class="col-12 col-sm-4">
                                    <g-field
                                        v-model="fields.price_returned"
                                        :app="app"
                                        :item="id"
                                        name="price_returned"
                                        title="Importe devuelto"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col text-right">
                            <a-button-group>
                                <a-popconfirm
                                    title="¿Estas seguro que deseas resetear el formulario?"
                                    @confirm="
                                        () => {
                                            resetConcepts()
                                            resetClient()
                                        }
                                    "
                                >
                                    <a-button>Resetear</a-button>
                                </a-popconfirm>
                                <a-button type="primary" html-type="submit" :loading="loading" :disabled="invalid">
                                    {{ id > 0 ? 'Modificar' : 'Añadir' }}
                                </a-button>
                            </a-button-group>
                        </div>
                    </div>
                </form>
            </validation-observer>
        </div>
    </div>
</template>

<script>
// import concepts from '../../mixins/concepts'
import client from '@/application/mixins/billing/client'
import calculations from '@/application/mixins/billing/calculations'
import articles from '@/application/mixins/billing/articles'
import concepts from '@/application/mixins/billing/concepts'
import series from '@/application/mixins/billing/series'

import invoice from './mixins/invoice'

import Selector from '@/application/components/select.vue'

export default {
    name: 'invoiceForm',
    mixins: [invoice, concepts, client, series, calculations, articles],
    components: {
        Selector,
    },
    data() {
        return {
            serieSelected: null,
            seriesNames: [],
            loadingSerial: false,
        }
    },
    async created() {
        this.events(true)

        // Load config
        // await this.loadConfig()

        // load invoice
        await this.load()
        const excludes = ['GASTOS', 'FACTURAS']
        await this.loadSerialNamesToSelect(excludes)

        // load new serial number
        if (this.id === 0) {
            await this.setSerial()
        }

        this.loading = false
    },
    computed: {
        changeSerieIsAvailable() {
            return this.$gestios.profile.Group.ID === -1
        },
    },
    beforeDestroy() {
        this.events(false)
    },
    methods: {
        async handlerRefreshSerial() {
            if (this.changeSerieIsAvailable && this.serieSelected) {
                await this.loadSerialNumber(this.serieSelected)
                return
            }
            await this.setSerial()
        },
        async setSerial() {
            try {
                const serieToLoad = `REPARTIDOR ${this.$gestios.profile.ID}`
                await this.loadSerialNumber(serieToLoad)
                this.serieSelected = serieToLoad
            } catch (error) {
                const serieToLoad = 'ALBARANES'
                await this.loadSerialNumber(serieToLoad)
                this.serieSelected = serieToLoad
            }
        },
    },
}
</script>

<style lang="scss">
@import './index.scss';
</style>
